import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import Image from 'next/image';
import posthog from 'posthog-js';
import { utils } from '@makeship/core';

import { S1, S2, S3 } from '../Typography';
import Countdown from '../Countdown';
import { getCreatorDropDetailsByHandle, parseFeaturedCollectionMetafields } from '../../utils/collections';
import { hasProductTag, parseProductMetafieldsFromCollection } from '../../utils/product';
import { ProductTag } from '../../types/common';
import Badge from '../../ui/atoms/Badge/badge';

const CollectionCard = styled.div`
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    width: calc(50% - 8px);
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    width: calc(50% - 14px);
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.headerBreakpoint}px) {
    width: calc(50% - 22px);
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.maxContent}px) {
    width: calc(33.3333% - 24px);
  }
`;

const Hyperlink = styled.a``;

const ImageWrapper = styled.div<{ backgroundColor?: string; hasBackgroundImage?: boolean }>`
  width: 100%;
  object-fit: cover;
  height: 168px;
  position: relative;
  border-radius: 6px;
  background: ${({ theme, hasBackgroundImage, backgroundColor }) =>
    !hasBackgroundImage && (backgroundColor || theme.colors.neutral2)};

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    &:hover {
      .gradient-overlay {
        opacity: 1;
      }
    }
  }
`;

const StyledImage = styled(Image)`
  border-radius: 6px;
`;

const TextWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 4px;
`;

const CreatorTitle = styled(S1)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-grow: 0;
  max-width: 80%;
`;

const ProductsOverlayContainer = styled.div<{ showProducts: boolean }>`
  position: absolute;
  display: flex;
  opacity: ${({ showProducts }) => (showProducts ? '1' : '0')};
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  gap: 4px;
  transition: opacity 0.3s ease;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    opacity: 0;
    &:hover {
      opacity: 1;
    }
  }
`;

const ProductImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  object-fit: cover;
  width: 70px;
  height: 70px;
  border-radius: 6px;
  box-shadow: 0px 2px 8px 0px rgba(20, 20, 20, 0.2);
  background: ${({ theme }) => utils.hexToRGBA(theme.colors.neutral1, 90)};
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.headerBreakpoint}px) {
    width: 100px;
    height: 100px;
  }
`;

const MoreText = styled.div`
  display: flex;
  flex-direction: column;
`;

const CountdownWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PillOverlayContainer = styled.div`
  position: absolute;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  top: 4px;
  right: 4px;
  padding: 4px 10px;
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => `linear-gradient(to top, ${theme.colors.neutral1} 0%, transparent 100%)`};
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 4px;
`;

type CollectionItemProps = {
  node: CollectionResponse;
  index: number;
  sectionName: string;
  showProducts: boolean;
  registerRef: (el: HTMLDivElement | null) => void;
  isUserLoggedIn: boolean;
};

const CollectionItem: React.FC<CollectionItemProps> = React.memo(
  ({ node, index, sectionName, showProducts, registerRef, isUserLoggedIn }: CollectionItemProps) => {
    const productEdges = node.products || [];
    const featuredProduct = productEdges[0]?.node;

    const { metafields } = node;
    const { headerBackgroundURL, backgroundColour, hasDiscount, productDropCollections } =
      parseFeaturedCollectionMetafields(metafields.edges);

    const [shouldEnableStretchGoals, setShouldEnableStretchGoals] = useState(false);
    const [hasAchievedStretchGoals, setHasAchievedStretchGoals] = useState(false);
    const productMetafields = (featuredProduct?.metafields ?? []) as Shopify.MetafieldConnection;
    const { enddate } = parseProductMetafieldsFromCollection(productMetafields.edges);

    const isNonStandardCampaign =
      hasProductTag(featuredProduct?.tags, ProductTag.Evergreen) ||
      hasProductTag(featuredProduct?.tags, ProductTag.Hybrid) ||
      hasProductTag(featuredProduct?.tags, ProductTag.Limited);

    const hasProductDropCollections = productDropCollections && productDropCollections.length > 0;

    useEffect(() => {
      if (!hasProductDropCollections) return;

      (async () => {
        const { shouldEnableStretchGoals, goalsAchieved } = await getCreatorDropDetailsByHandle(node.handle);

        setShouldEnableStretchGoals(shouldEnableStretchGoals);
        setHasAchievedStretchGoals(goalsAchieved);
      })();
    }, []);

    const shouldShowStretchGoalBadge = hasProductDropCollections && shouldEnableStretchGoals;

    if (!featuredProduct) return null;

    const NUMBER_OF_ITEMS_TO_SHOW = 3;
    const productCount = productEdges.length;
    const displayedProducts = productEdges.slice(0, NUMBER_OF_ITEMS_TO_SHOW);
    const hasMore = productCount > NUMBER_OF_ITEMS_TO_SHOW;

    return (
      <CollectionCard key={node.handle}>
        <Link href={`/shop/creator/${node.handle}`} passHref legacyBehavior>
          <Hyperlink
            onClick={() => {
              posthog.capture('featuredCreator_collectionCard_click', {
                creator_handle: node.handle,
                creator_title: node.title,
                is_logged_in: isUserLoggedIn,
                section_name: sectionName,
                link_href: `/shop/creator/${node.handle}`,
              });
            }}
          >
            <ImageWrapper backgroundColor={backgroundColour} hasBackgroundImage={!!headerBackgroundURL}>
              {headerBackgroundURL && (
                <>
                  <StyledImage
                    src={headerBackgroundURL}
                    layout="fill"
                    objectFit="cover"
                    alt={`${node.title} collection`}
                    priority
                    quality={90}
                    unoptimized
                  />
                  <GradientOverlay className="gradient-overlay" />
                </>
              )}

              {(shouldShowStretchGoalBadge || hasDiscount) && (
                <PillOverlayContainer>
                  <Badge color={hasDiscount ? 'red' : 'blurple'}>
                    {hasDiscount ? 'Discounts' : hasAchievedStretchGoals ? 'Free Gift' : 'Unlock Gift'}
                  </Badge>
                </PillOverlayContainer>
              )}

              <ProductsOverlayContainer
                showProducts={showProducts}
                ref={registerRef}
                data-id={node.handle}
                data-index={index}
              >
                {productCount > 0 &&
                  displayedProducts.map((productEdge: Shopify.ProductEdge) => (
                    <ProductImageWrapper key={productEdge.node.id}>
                      <StyledImage
                        src={productEdge.node.featuredMedia?.image?.transformedSrc || ''}
                        alt={productEdge.node.title}
                        height={100}
                        width={100}
                        quality={90}
                        priority
                        unoptimized
                      />
                    </ProductImageWrapper>
                  ))}
                {hasMore && (
                  <ProductImageWrapper>
                    <MoreText>
                      <S2>& More!</S2>
                      <S2>➡️</S2>
                    </MoreText>
                  </ProductImageWrapper>
                )}
              </ProductsOverlayContainer>
            </ImageWrapper>

            <TextWrapper>
              <CreatorTitle>{node.title}</CreatorTitle>
              {!!enddate && !isNonStandardCampaign && (
                <CountdownWrapper>
                  🕓
                  <S3>
                    <Countdown date={enddate} />
                  </S3>
                </CountdownWrapper>
              )}
            </TextWrapper>
          </Hyperlink>
        </Link>
      </CollectionCard>
    );
  },
);

export default CollectionItem;
